import { PaymentMethodEnum } from "@/shared/utils/enum";
import { toast } from "react-toastify";
import { useSelectedCartItems } from "../atoms/useSplitPaymentV2Atom";
import { useModalMeta } from "../atoms/useModal";
import { useSetPriorityModal } from "../atoms/usePriorityModal";
import usePaymentLedgerDelete from "../mutations/paymentLedger/usePaymentLedgerDelete";
import usePaymentLedgerPaymentPut from "../mutations/paymentLedger/usePaymentLedgerPaymentPut";
import {
  CartItem,
  PaymentLedger,
  SelectedCartItem,
  SplitPaymentStates,
} from "../utils/interfaces";
import { useCartItemTotal } from "./useCartHook";
import { useTranslation } from "react-i18next";
import { getRandomNegativeId, splitAmount } from "../utils/helpers";
import useUnsavedPrinterMutation from "../mutations/printReceipt/useUnsavedPrinterMutation";
import usePosOrderSubmitDataHook from "./usePosOrderSubmitDataHook";
import useOpenCashDrawerHook from "./useOpenCashDrawerHook";
import usePaymentLedgerHook from "./usePaymentLedgerHook";
import usePaymentLedgerPost from "../mutations/paymentLedger/usePaymentLedgerPost";
import usePaymentHook from "./usePaymentHook";
import {
  useReadSplitPaymentOrdersState,
  useSetSplitPaymentOrdersState,
} from "../atoms/useSplitPaymentV2Atom";
import useCashDroPaymentHook from "./useCashDroPaymentHook";
import { logCashDro } from "../utils/cashDro";

export const defaultLedgerPayment: PaymentLedger = {
  item_id: 0,
  payment_amount: "",
  payment_tip: "",
  payment_service_fee: "",
  payment_method_id: 0,
  is_paid: false,
  is_removed: false,
  write_account_id: { 1: 1 },
};

const useSplitPaymentHook = (splitPaymentCart?: CartItem[]) => {
  const { t } = useTranslation("");
  const [{ totalAmount }] = useModalMeta();
  const getCartItemTotal = useCartItemTotal();
  const setPriorityModal = useSetPriorityModal();
  const [selectedCartItems, setSelectedCartItems] = useSelectedCartItems();
  const { orderId, data: defaultLedgers, isSession } = usePaymentLedgerHook();
  const paymentLedgerPut = usePaymentLedgerPaymentPut();
  const paymentLedgerDelete = usePaymentLedgerDelete();
  const createPaymentLedgerMutation = usePaymentLedgerPost();
  const printOnTheFly = useUnsavedPrinterMutation();
  const createSubmitData = usePosOrderSubmitDataHook();
  const openCashDrawer = useOpenCashDrawerHook();
  const { isOnlinePayment } = usePaymentHook();
  const cashDro = useCashDroPaymentHook();

  const { paymentLedgers, splitType, activeIndex } =
    useReadSplitPaymentOrdersState();
  const setSplitPaymentState = useSetSplitPaymentOrdersState();

  const getNextState = (
    ledgers: PaymentLedger[],
    index: number
  ): Partial<SplitPaymentStates> => {
    if (index < ledgers.length - 1) {
      return { activeIndex: index + 1 };
    }

    const paidTotal = ledgers.reduce(
      (acc, ledger) => acc + parseFloat(ledger.payment_amount || "0"),
      0
    );

    if (Math.abs(paidTotal - totalAmount) > 0.01) {
      return {
        paymentLedgers: [
          ...ledgers,
          {
            ...defaultLedgerPayment,
            payment_amount: (totalAmount - paidTotal).toFixed(2),
            item_id: getRandomNegativeId(),
          },
        ],
        activeIndex: index + 1,
      };
    }

    return {};
  };

  const handleNext = () => {
    setSplitPaymentState(getNextState(paymentLedgers ?? [], activeIndex));
  };

  const handleBack = () => {
    if (activeIndex > 0) {
      setSplitPaymentState({ activeIndex: activeIndex - 1 });
    }
  };

  const printLedger = (ledger: PaymentLedger) => {
    const _cart = selectedCartItems?.filter(
      (item) => item.ledgerId === ledger.item_id
    );
    const cart = _cart.map((c) => {
      const item = splitPaymentCart?.find((item) => item.item_id === c.item_id);
      return {
        ...item,
        quantity: c.quantity,
      };
    }) as CartItem;

    const submitData = createSubmitData({
      onTheFlyPrinting: true,
      customCart: cart as CartItem[],
      ledger,
    });

    printOnTheFly.mutate(submitData);
  };

  const handlePayment = async (
    ledger: PaymentLedger,
    {
      onLedgerCreated,
    }: { onLedgerCreated?: (ledger: PaymentLedger) => void } = {}
  ) => {
    let updatedLedger: PaymentLedger;
    let newLedger: PaymentLedger;
    let newLedgers: PaymentLedger[];

    const shouldCreateLedger = ledger.item_id < 0;
    try {
      if (shouldCreateLedger) {
        const createdLedgers = await createLedger(ledger);
        newLedger = { ...ledger, ...createdLedgers[createdLedgers.length - 1] };
        onLedgerCreated?.(newLedger);
        newLedgers = updateLedger(ledger.item_id, newLedger);
        await handleUpdatePayment(newLedger);
        updatedLedger = newLedger;
      } else {
        await handleUpdatePayment(ledger);
        updatedLedger = ledger;
      }
      if (updatedLedger.payment_method_id === PaymentMethodEnum.POS_TERMINAL) {
        setPriorityModal("pendingPayment", {
          orderId: isSession ? undefined : orderId,
          sessionId: isSession ? orderId : undefined,
          ledgerId: updatedLedger.item_id,
          isSplitPayment: true,
        });
      }
    } catch (error) {
      const updateId = newLedger?.item_id ?? ledger.item_id;
      const previousLedgerData = defaultLedgers?.find(
        (l) => l.item_id === updateId
      );
      if (previousLedgerData) {
        updateLedger(updateId, previousLedgerData, newLedgers);
      }
    }
  };

  const handlePaymentWithCashDro = async (ledger: PaymentLedger) => {
    const amount =
      (parseFloat(ledger.payment_amount) || 0) +
      (parseFloat(ledger.payment_tip) || 0) +
      (parseFloat(ledger.payment_service_fee) || 0);

    logCashDro("cash split payment", {
      ledgerId: ledger.item_id,
      orderId,
      isSession,
      paymentAmount: ledger.payment_amount,
      tip: ledger.payment_tip,
      serviceFee: ledger.payment_service_fee,
      amount,
    });

    return cashDro.collect({
      amount,
      aliasId: `${isSession ? "S" : "O"}${orderId}`,
      onPaid: () =>
        handlePayment(ledger, {
          onLedgerCreated: (created) => {
            logCashDro("split ledger created and paid:", {
              ledgerId: ledger.item_id,
              createdLedgerId: created.item_id,
            });
          },
        }),
    });
  };

  const handleUpdatePayment = async (ledger: PaymentLedger) => {
    return paymentLedgerPut.mutateAsync({
      isSession: isSession ? 1 : 0,
      payment_service_fee: ledger.payment_service_fee || "0",
      payment_tip: ledger.payment_tip || "0",
      transaction_email: ledger.transaction_email,
      paymentLedgerId: ledger.item_id,
      paymentMethodId: ledger.payment_method_id,
    });
  };

  const createLedger = async (ledger: PaymentLedger) => {
    return createPaymentLedgerMutation.mutateAsync({
      orderId: orderId,
      isSession: isSession,
      paymentAmount: ledger.payment_amount,
    });
  };

  const deleteLedger = (ledger: PaymentLedger) => {
    if (ledger.item_id < 0) {
      const newPaymentLedgers = paymentLedgers.filter(
        (l) => l.item_id !== ledger.item_id
      );
      setSplitPaymentState({
        paymentLedgers: newPaymentLedgers,
        splitCount: newPaymentLedgers.length,
      });
    } else {
      paymentLedgerDelete.mutate(
        { orderId: orderId, ledgerId: ledger.item_id },
        {
          onSuccess: () => {
            const newPaymentLedgers = paymentLedgers.filter(
              (l) => l.item_id !== ledger.item_id
            );
            setSplitPaymentState({
              paymentLedgers: newPaymentLedgers,
              splitCount: newPaymentLedgers.length,
            });
          },
        }
      );
    }
  };

  /**
   * Update a ledger item by id with partial fields.
   * @param id Ledger item_id to update
   * @param updates Partial fields to update
   */
  const updateLedger = (
    id: number,
    updates: Partial<PaymentLedger>,
    customList?: PaymentLedger[]
  ): PaymentLedger[] => {
    const newPaymentLedgers = (customList ?? paymentLedgers).map((ledger) =>
      ledger.item_id === id ? { ...ledger, ...updates } : ledger
    );
    setSplitPaymentState({ paymentLedgers: newPaymentLedgers });
    return newPaymentLedgers;
  };

  // Group products by paymentId for cart splitting
  const handleCartSplit = (cartItem: CartItem, ledgerId: number) => {
    let newSelectedCartItems: SelectedCartItem[] = [];

    // Find total quantity assigned to that product's splits
    const totalAssigned = selectedCartItems
      .filter((item) => item.item_id === cartItem.item_id)
      .reduce((acc, curr) => acc + (curr.quantity || 1), 0);
    const maxQuantity = cartItem.quantity || 1;

    // Find current quantity of clicked split
    const splitCartItem = selectedCartItems.find(
      (item) => item.item_id === cartItem.item_id && item.ledgerId === ledgerId
    );

    // If clicked split's quantity equals maxQuantity, remove that split
    if (splitCartItem?.quantity === maxQuantity) {
      newSelectedCartItems = selectedCartItems.filter(
        (item) =>
          !(item.item_id === cartItem.item_id && item.ledgerId === ledgerId)
      );
    } else {
      // If same ledgerId and productId exists, increase quantity, otherwise add new
      const existingIndex = selectedCartItems.findIndex(
        (item) =>
          item.item_id === cartItem.item_id && item.ledgerId === ledgerId
      );

      if (totalAssigned < maxQuantity) {
        // Normal addition
        if (existingIndex !== -1) {
          const newCartSplit = [...selectedCartItems];
          newCartSplit[existingIndex] = {
            ...newCartSplit[existingIndex],
            quantity: (newCartSplit[existingIndex].quantity || 1) + 1,
          };
          newSelectedCartItems = newCartSplit;
          /*  setSelectedCartItems(newCartSplit); */
        } else {
          newSelectedCartItems = [
            ...selectedCartItems,
            {
              item_id: cartItem.item_id,
              quantity: 1,
              ledgerId: ledgerId,
              price: `${getCartItemTotal(cartItem) / cartItem.quantity}`,
            },
          ];
        }
      } else if (totalAssigned === maxQuantity) {
        // If total assignment equals product quantity, reduce from another split and add to clicked split
        const otherSplitIndex = selectedCartItems.findIndex(
          (item) =>
            item.item_id === cartItem.item_id &&
            item.ledgerId !== ledgerId &&
            (item.quantity || 1) > 0
        );

        if (otherSplitIndex !== -1) {
          const newCartSplit = JSON.parse(JSON.stringify(selectedCartItems));

          // Add to clicked split
          if (existingIndex !== -1) {
            const split = newCartSplit[existingIndex] ?? newCartSplit[0];
            newCartSplit[existingIndex] = {
              ...split,
              quantity: (split.quantity || 1) + 1,
            };
          } else {
            newCartSplit.push({
              item_id: cartItem.item_id,
              quantity: 1,
              ledgerId: ledgerId,
              price: `${getCartItemTotal(cartItem) / cartItem.quantity}`,
            });
          }

          // Reduce from other split
          if ((newCartSplit[otherSplitIndex].quantity || 1) > 1) {
            newCartSplit[otherSplitIndex] = {
              ...newCartSplit[otherSplitIndex],
              quantity: (newCartSplit[otherSplitIndex].quantity || 1) - 1,
            };
          } else {
            newCartSplit.splice(otherSplitIndex, 1);
          }

          newSelectedCartItems = newCartSplit;
        }
      }
      // If cannot reduce from other split, do nothing
    }

    setSelectedCartItems(newSelectedCartItems);

    const newPaymentLedgers = paymentLedgers.map((ledger) => {
      const ledgerWithSelectedCartItems = newSelectedCartItems.filter(
        (s) => s.ledgerId === ledger.item_id
      );
      const totalAmount = ledgerWithSelectedCartItems.reduce(
        (sum, item) =>
          sum + (item.quantity || 1) * parseFloat(item.price || "0"),
        0
      );
      return { ...ledger, payment_amount: totalAmount.toFixed(2) };
    });

    setSplitPaymentState({ paymentLedgers: newPaymentLedgers });
  };

  const getTotalAmountByPaidStatus = (
    ledgers: PaymentLedger[],
    isPaid?: boolean
  ) => {
    return ledgers.reduce(
      (acc, ledger) =>
        acc +
        (isPaid
          ? ledger.is_paid || ledger.item_id > 0
            ? parseFloat(ledger.payment_amount || "0")
            : 0
          : parseFloat(ledger.payment_amount || "0")),
      0
    );
  };

  const getTotalSplitAmount = (ledgers: PaymentLedger[]) => {
    const totalPaidLedgerAmount = getTotalAmountByPaidStatus(ledgers, true);
    const totalSplitAmount = totalAmount - totalPaidLedgerAmount;
    return totalSplitAmount;
  };

  const createUnpaidLedgers = (
    paidLedgers: PaymentLedger[],
    splitCount: number
  ): PaymentLedger[] => {
    const count = splitCount - paidLedgers.length;
    if (count < 0) return [];

    const totalSplitAmount = getTotalSplitAmount(paidLedgers);
    const split: number[] = splitAmount(totalSplitAmount, count);

    return Array(count)
      .fill(null)
      .map((_, index) => ({
        ...defaultLedgerPayment,
        item_id: getRandomNegativeId(),
        payment_amount: split[index].toFixed(2),
      }));
  };

  const onSuccessPayment = (
    ledger: PaymentLedger,
    newLedgers: PaymentLedger[]
  ) => {
    if (ledger.payment_method_id === PaymentMethodEnum.CASH) {
      if (cashDro.isActive) {
        logCashDro(
          "cash drawer not opened, CashDro handled the split payment",
          {
            ledgerId: ledger.item_id,
          }
        );
      } else {
        openCashDrawer();
      }
    }
    let updatedLedgers = newLedgers;

    if (splitType === "custom") {
      const unpaidLedgers = newLedgers.filter((i) => i.item_id < 0);
      const totalSplitAmount = getTotalSplitAmount(newLedgers);
      const split: number[] = splitAmount(
        totalSplitAmount,
        unpaidLedgers.length
      );
      updatedLedgers = newLedgers.map((l) =>
        l.item_id < 0
          ? { ...l, payment_amount: split.pop()?.toFixed(2) || "0.00" }
          : l
      );
    }

    const paidIndex = updatedLedgers.findIndex(
      (l) => l.item_id === ledger.item_id
    );
    setSplitPaymentState({
      paymentLedgers: updatedLedgers,
      ...getNextState(
        updatedLedgers,
        paidIndex === -1 ? activeIndex : paidIndex
      ),
    });

    toast.success(
      isOnlinePayment(ledger.payment_method_id)
        ? t("posOrders.paymentLinkGeneratedAndSent")
        : t("posOrders.paymentSuccessful")
    );
  };

  return {
    handlePaymentWithCashDro,
    printLedger,
    handleCartSplit,
    updateLedger,
    deleteLedger,
    handlePayment,
    onSuccessPayment,
    handleNext,
    handleBack,
    selectedCartItems,
    createUnpaidLedgers,
    ledgerTotal: getTotalAmountByPaidStatus(paymentLedgers, false),
    isPending:
      paymentLedgerPut.isPending || createPaymentLedgerMutation.isPending,
  };
};

export default useSplitPaymentHook;
